// Shared "export this on-screen table" helpers for the Data Converter's
// reconciliation/DQ report views — every screen that shows a scannable
// table to a (possibly non-technical) reviewer should let them save it
// off for a ticket/email/audit trail instead of only ever being readable
// on-screen.
//
// PDF uses jsPDF + jspdf-autotable (already a dependency, already used by
// DataConverterView's own Migration Summary export — see its
// handleDownloadMigrationSummary). Excel has no equivalent library in this
// app; rather than add one for a handful of report tables, this generates
// a real "SpreadsheetML 2003" XML workbook (Microsoft's own documented
// Excel-native XML format) with a `.xls` extension and the matching
// `application/vnd.ms-excel` MIME type — Excel opens it directly with no
// "file format doesn't match extension" prompt, unlike the common
// HTML-table-saved-as-.xls trick.
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export type ExportCell = string | number | null | undefined;

function cellText(value: ExportCell): string {
  return value === null || value === undefined ? "" : String(value);
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Renders one table as a landscape PDF (auto-paginating, auto-sized
 * columns) and triggers a browser download. */
export function downloadTableAsPdf(
  title: string,
  columns: string[],
  rows: ExportCell[][],
  filename: string,
): void {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt" });
  doc.setFontSize(14);
  doc.setTextColor(0);
  doc.text(title, 40, 36);
  doc.setFontSize(8);
  doc.setTextColor(120);
  doc.text(`Generated ${new Date().toLocaleString()}`, 40, 50);
  doc.setTextColor(0);

  autoTable(doc, {
    startY: 62,
    margin: { left: 40, right: 40 },
    head: [columns],
    body: rows.map((row) => row.map(cellText)),
    headStyles: { fillColor: [40, 40, 60] },
    styles: { fontSize: 8, cellPadding: 4 },
  });
  doc.save(filename);
}

/** Escapes text for inclusion inside SpreadsheetML XML (a stricter
 * subset of XML entity escaping than HTML needs). */
function xmlEscape(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function spreadsheetMlCell(value: ExportCell): string {
  const text = cellText(value);
  const isNumeric = text !== "" && !Number.isNaN(Number(text));
  const type = isNumeric ? "Number" : "String";
  return `<Cell><Data ss:Type="${type}">${xmlEscape(text)}</Data></Cell>`;
}

/** Renders one table as a genuine Excel-native "SpreadsheetML 2003" .xls
 * workbook (one worksheet) and triggers a browser download. */
export function downloadTableAsExcel(
  sheetName: string,
  columns: string[],
  rows: ExportCell[][],
  filename: string,
): void {
  const headerRow = `<Row>${columns.map((c) => spreadsheetMlCell(c)).join("")}</Row>`;
  const bodyRows = rows
    .map(
      (row) =>
        `<Row>${row.map((cell) => spreadsheetMlCell(cell)).join("")}</Row>`,
    )
    .join("");
  // Sheet names in SpreadsheetML can't contain []/\?*: or exceed 31 chars.
  const safeSheetName = sheetName.replace(/[[\]/\\?*:]/g, " ").slice(0, 31);
  const xml = `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
  <Worksheet ss:Name="${xmlEscape(safeSheetName)}">
    <Table>
      ${headerRow}
      ${bodyRows}
    </Table>
  </Worksheet>
</Workbook>`;
  const blob = new Blob([xml], { type: "application/vnd.ms-excel" });
  triggerDownload(blob, filename);
}
